/**
 * Modern Sidebar Component
 * Replaces jQuery-based sidebar functionality with vanilla JavaScript
 */

class Sidebar {
  constructor() {
    this.sidebar = document.querySelector('.sidebar');
    this.sidebarMenu = document.querySelector('.sidebar .sidebar-menu');
    this.sidebarToggleLinks = document.querySelectorAll('.sidebar-toggle a');
    this.sidebarToggleById = document.querySelector('#sidebar-toggle');
    this.app = document.querySelector('.app');
    
    this.init();
  }

  init() {
    if (!this.sidebar || !this.sidebarMenu) {
      return;
    }

    this.setupMenuToggle();
    this.setupSidebarToggle();
    this.setActiveLink();
  }

  /**
   * Setup dropdown menu functionality
   */
  setupMenuToggle() {
    const menuLinks = this.sidebarMenu.querySelectorAll('li.dropdown > a');
    
    menuLinks.forEach(link => {
      const listItem = link.parentElement;
      const dropdownMenu = listItem.querySelector('.dropdown-menu');
      
      if (!dropdownMenu) return;
      
      // Remover listeners anteriores para evitar duplicados
      const newLink = link.cloneNode(true);
      link.parentNode.replaceChild(newLink, link);
      
      // Siempre usar CLICK para toggle, tanto en modo normal como colapsado
      newLink.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation(); // Prevenir que otros listeners se ejecuten
        
        // Marcar que este click es del sidebar para que otros listeners lo ignoren
        e.target._isSidebarDropdown = true;
        e._isSidebarDropdown = true;
        
        // Toggle del dropdown con click
        if (listItem.classList.contains('open')) {
          this.closeDropdown(listItem, dropdownMenu);
        } else {
          this.closeAllDropdowns();
          this.openDropdown(listItem, dropdownMenu);
        }
      }, true); // Usar captura para ejecutarse antes que otros listeners
    });
    
    // También stop propagation on clicks inside dropdown menus to prevent closing
    const dropdownMenus = this.sidebarMenu.querySelectorAll('.dropdown-menu');
    dropdownMenus.forEach(menu => {
      menu.addEventListener('click', (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, true);
    });
  }

  /**
   * Open dropdown with smooth animation
   */
  openDropdown(listItem, dropdownMenu) {
    // Agregar clase open para que el CSS muestre el dropdown
    listItem.classList.add('open');
    
    // Verificar si el sidebar está colapsado
    const isCollapsed = this.app && this.app.classList.contains('is-collapsed');
    
    if (isCollapsed) {
      // Cuando está colapsado, simplemente mostrar el dropdown
      // El CSS se encargará del posicionamiento
      dropdownMenu.style.display = 'block';
      dropdownMenu.style.visibility = 'visible';
      dropdownMenu.style.opacity = '1';
      dropdownMenu.style.position = 'relative';
      dropdownMenu.style.height = 'auto';
      dropdownMenu.style.overflow = 'visible';
    } else {
      // Cuando está expandido, usar animación
      dropdownMenu.style.display = 'block';
      dropdownMenu.style.position = 'relative';
      dropdownMenu.style.left = 'auto';
      dropdownMenu.style.height = '0px';
      dropdownMenu.style.overflow = 'hidden';
      
      // Get the natural height
      const height = dropdownMenu.scrollHeight;
      
      // Animate to full height
      dropdownMenu.animate([
        { height: '0px' },
        { height: `${height}px` },
      ], {
        duration: 200,
        easing: 'ease-out',
      }).onfinish = () => {
        dropdownMenu.style.height = 'auto';
        dropdownMenu.style.overflow = 'visible';
      };
    }
  }

  /**
   * Close dropdown with smooth animation
   */
  closeDropdown(listItem, dropdownMenu) {
    // Remover clase open
    listItem.classList.remove('open');
    
    // Verificar si el sidebar está colapsado
    const isCollapsed = this.app && this.app.classList.contains('is-collapsed');
    
    if (isCollapsed) {
      // Cuando está colapsado, simplemente ocultar
      dropdownMenu.style.display = 'none';
      dropdownMenu.style.visibility = 'hidden';
      dropdownMenu.style.opacity = '0';
    } else {
      // Cuando está expandido, usar animación
      const height = dropdownMenu.scrollHeight;
      
      dropdownMenu.style.height = `${height}px`;
      dropdownMenu.style.overflow = 'hidden';
      
      dropdownMenu.animate([
        { height: `${height}px` },
        { height: '0px' },
      ], {
        duration: 200,
        easing: 'ease-in',
      }).onfinish = () => {
        dropdownMenu.style.display = 'none';
        dropdownMenu.style.height = '';
        dropdownMenu.style.overflow = '';
      };
    }
  }

  /**
   * Close all open dropdowns
   */
  closeAllDropdowns() {
    const openItems = this.sidebarMenu.querySelectorAll('li.open');
    
    openItems.forEach(item => {
      const dropdownMenu = item.querySelector('.dropdown-menu');
      if (dropdownMenu) {
        this.closeDropdown(item, dropdownMenu);
      }
      
      // Also remove the has-active-child class
      item.classList.remove('has-active-child');
    });
  }

  /**
   * Setup sidebar toggle functionality
   * NOTA: Si ya hay un listener inline (de base.php), no agregar otro
   */
  setupSidebarToggle() {
    // Si ya está inicializado por el script inline, no hacer nada
    if (window._sidebarInitialized) {
      return;
    }
    
    // Prevenir múltiples inicializaciones
    if (this.sidebarToggleById && this.sidebarToggleById._sidebarListener) {
      return; // Ya está inicializado
    }
    
    // Handle mobile sidebar toggle links (inside .sidebar-toggle divs)
    this.sidebarToggleLinks.forEach(link => {
      if (link && this.app && !link._sidebarListener) {
        // Remover listeners anteriores
        const newLink = link.cloneNode(true);
        link.parentNode.replaceChild(newLink, link);
        
        newLink.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.toggleSidebar();
        }, true);
        
        newLink._sidebarListener = true;
      }
    });

    // Handle the main topbar sidebar toggle
    if (this.sidebarToggleById && this.app) {
      // Remover listeners anteriores
      const newToggle = this.sidebarToggleById.cloneNode(true);
      this.sidebarToggleById.parentNode.replaceChild(newToggle, this.sidebarToggleById);
      
      // Actualizar referencia
      this.sidebarToggleById = newToggle;
      
      newToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.toggleSidebar();
      }, true);
      
      newToggle._sidebarListener = true;
    }
  }

  /**
   * Toggle sidebar and handle resize events properly
   */
  toggleSidebar() {
    if (!this.app) return;
    
    // Prevenir múltiples toggles simultáneos
    if (this._isToggling) {
      return;
    }
    
    this._isToggling = true;
    
    const wasCollapsed = this.app.classList.contains('is-collapsed');
    this.app.classList.toggle('is-collapsed');
    const isCollapsed = this.app.classList.contains('is-collapsed');
    
    // Cuando se colapsa, cerrar todos los dropdowns
    if (isCollapsed) {
      this.closeAllDropdowns();
      
      // Limpiar estilos inline de los dropdowns
      const allDropdownMenus = this.sidebarMenu.querySelectorAll('.dropdown-menu');
      allDropdownMenus.forEach(menu => {
        menu.removeAttribute('style');
      });
    }
    
    // Only trigger resize for masonry, but avoid chart redraw issues
    setTimeout(() => {
      // Dispatch a custom event instead of generic resize to avoid chart issues
      window.dispatchEvent(new CustomEvent('sidebar:toggle', {
        detail: { collapsed: isCollapsed },
      }));
      
      // Still trigger resize for masonry but with a specific check
      if (window.EVENT) {
        window.dispatchEvent(window.EVENT);
      }
      
      this._isToggling = false;
    }, 300);
  }

  /**
   * Set active link based on current URL
   */
  setActiveLink() {
    // Remove active class from all nav items (including dropdown items)
    const allNavItems = this.sidebar.querySelectorAll('.nav-item');
    allNavItems.forEach(item => {
      item.classList.remove('actived');
    });

    // Close all dropdowns first
    this.closeAllDropdowns();

    // Get current page filename
    const currentPath = window.location.pathname;
    const currentPage = currentPath.split('/').pop() || 'index.html';
    
    
    // Find and activate the correct nav item
    const allLinks = this.sidebar.querySelectorAll('a[href]');
    
    allLinks.forEach(link => {
      const href = link.getAttribute('href');
      if (!href || href === 'javascript:void(0);' || href === 'javascript:void(0)') return;
      
      // Extract filename from href
      const linkPage = href.split('/').pop();
      
      if (linkPage === currentPage) {
        const navItem = link.closest('.nav-item');
        if (navItem) {
          navItem.classList.add('actived');
          
          // If this is inside a dropdown, handle parent dropdown specially
          const parentDropdown = navItem.closest('.dropdown-menu');
          if (parentDropdown) {
            const parentDropdownItem = parentDropdown.closest('.nav-item.dropdown');
            if (parentDropdownItem) {
              // Open the parent dropdown
              parentDropdownItem.classList.add('open');
              parentDropdown.style.display = 'block';
              
              // Add special styling to indicate parent has active child
              parentDropdownItem.classList.add('has-active-child');
            }
          }
        }
      }
    });
    
  }

  /**
   * Public method to refresh active links (useful for SPA navigation)
   */
  refreshActiveLink() {
    this.setActiveLink();
  }

  /**
   * Public method to toggle sidebar programmatically
   */
  toggle() {
    if (this.app) {
      this.app.classList.toggle('is-collapsed');
    }
  }

  /**
   * Public method to check if sidebar is collapsed
   */
  isCollapsed() {
    return this.app ? this.app.classList.contains('is-collapsed') : false;
  }
}

export default Sidebar; 